import type { StrokePoints } from "../handwriting/stroke-shape.js";
import type { CharacterMarker, MarkingAsked } from "./xiezi-check.js";
import { type MarkedPass, markedPass } from "./xiezi-marked.js";
import {
  type DrillProgress,
  drillStart,
  inkStays,
} from "../handwriting/xiezi-stages.js";
import type { NamedCharacter } from "./xiezi-stage-words.js";
import type { XieziSquare } from "./xiezi-square.js";

/**
 * Marking the drill's fourth stage, on a press of Check and on its own.
 *
 * Ink with as many strokes as the character is marked without a press, and
 * the drill acts on that mark only where it passes. A press of Check waits
 * for a marking already under way, and marks nothing where that marking has
 * moved the reader on, so two presses mark once. A mark that comes back after
 * the reader has moved, by a press, a Clear or an Exercise changing the
 * character, is dropped, and so is one whose ink has changed since, by an
 * Undo or another stroke.
 * `xiezi-drill.ts` is the drill, which reports each move through
 * {@link DrillMarking.moved} and applies a mark through `apply`.
 */

/** What a marking reads and what it hands a mark to. */
export interface DrillMarkingProperties {
  readonly marker: CharacterMarker;

  /** The drill's parts, whose square holds the ink and its stroke count. */
  readonly parts: { readonly square: Pick<XieziSquare, "ink" | "strokeCount"> };

  /** Move the reader by a mark that stands. */
  readonly apply: (pass: MarkedPass) => void;
}

/** The fourth stage's markings over one drill. */
export class DrillMarking {
  readonly #properties: DrillMarkingProperties;
  #named: NamedCharacter = { character: "" };
  #progress: DrillProgress = drillStart();

  /** How many moves the reader has made, which a mark coming back checks. */
  #moves = 0;

  /** The marking under way, with its mark applied, for a press to wait on. */
  #under: Promise<void> | undefined;

  constructor(properties: DrillMarkingProperties) {
    this.#properties = properties;
  }

  /**
   * The reader has moved, to `progress` on `named`.
   * @param named - The character in the square.
   * @param progress - Where the reader is now.
   */
  moved(named: NamedCharacter, progress: DrillProgress): void {
    this.#named = named;
    this.#progress = progress;
    this.#moves += 1;
  }

  /**
   * Mark the ink without a press, once it has every stroke of the character,
   * in the stage where the ink stays. The stages that snap mark each stroke
   * as it lands, and this leaves them alone.
   */
  whenWhole(): void {
    const { square } = this.#properties.parts;

    if (inkStays(this.#progress) && square.ink.length === square.strokeCount) {
      void this.#mark("automatic");
    }
  }

  /**
   * Mark the ink on a press of Check, after any marking under way. The press
   * is under way itself from the moment it is made, so a second press waits
   * for it and marks nothing once it has moved the reader.
   */
  async pressed(): Promise<void> {
    const before = this.#moves;
    const waiting = this.#under;
    const marking = (async (): Promise<void> => {
      await waiting;

      if (before === this.#moves) {
        await this.#marked("pressed");
      }
    })();

    this.#under = marking;
    await marking;
  }

  async #mark(asked: MarkingAsked): Promise<void> {
    const marking = this.#marked(asked);

    this.#under = marking;
    await marking;
  }

  async #marked(asked: MarkingAsked): Promise<void> {
    const { marker, parts, apply } = this.#properties;
    const before = this.#moves;
    const ink = [...parts.square.ink];
    const checked = await marker(this.#named.character, ink, asked);
    const stands = before === this.#moves && sameInk(ink, parts.square.ink);

    if (stands && (asked === "pressed" || checked.passed)) {
      apply(markedPass(this.#progress, checked));
    }
  }
}

/** Whether the square holds the very strokes that were marked, and no others. */
function sameInk(
  marked: readonly StrokePoints[],
  now: readonly StrokePoints[],
): boolean {
  return (
    marked.length === now.length &&
    marked.every((stroke, at) => stroke === now[at])
  );
}
